import React, { Component } from 'react'
import _ from 'lodash'
import { HashRouter, Route, Switch, Redirect } from 'react-router-dom'
import PQueue from './vendor/p-queue'
import moment from 'moment'
import './css/App.css'
import AppStyled from  './styles/App'
import I18n from './lib/i18n'
import { Provider } from './lib/StoreContext'
import { Cart, OrderHistory, ProductSearchResult, ProductGroups, ThankYou } from './containers'
import Alert from './components/Alert'
import * as API from './api'
import Config from './configs'
import 'bootstrap/dist/css/bootstrap.min.css'
import PageMiddleSpinner from './components/PageMiddleSpinner'
import Text from './components/Text'
import OfflineBanner from './components/OfflineBanner'
import withStore from './lib/withStore'
import * as ScrollViewControl from './lib/scrollViewControl'

class App extends Component {
  constructor(props) {
    super(props)
    this.recursiveFetchOrdersState = null
    this._flash = null
    this.state = {
      save: this.saveToStore,
      checkOrdersStatus: this.checkOrdersStatus,
      cart_total_items: 0,
      alert_message: '',
      content_groups: [],
      product_group_titles_hash: {},
      system_settings: {},
      fetch_databin_loading: true,
      loading_system_settings: true,
      orders: [],
      loading_orders: false,
      go_to_thank_you: false,
      go_to_order_history: false,
      session_expired: false,
    }
    this.fetch_order_status_queue = new PQueue({ concurrency: 1 })
  }

  componentDidMount() {
    window.addEventListener('kick_user', () => {
      const has_orders = !_.isEmpty(API.getStorage('completed_orders'))

      if (has_orders) {
        this.setState({ go_to_order_history: true })
      } else {
        this.setState({ go_to_thank_you: true, session_expired: true })
      }
    })

    this.updateLocale()
    this.saveSystemSettingToStore()
    this.kickOffTimerToFetchOrderState()
    this.updateDatabinStatusToClosed()
  }

  componentDidUpdate(prevProps, prevState, snapShot) {
    // prevent scrolling freeze when changing route after bottom sheet is opened
    window.onpopstate = () => {
      ScrollViewControl.preventScrolling()
    }

    !!prevState.go_to_thank_you && this.setState({ go_to_thank_you: false })
  }

  updateLocale = () => {
    const lang = I18n.currentLocale()
    const html = document.getElementsByTagName('html')[0]
    const body = document.body
    I18n.setLocale(lang)
    html.setAttribute('lang', lang)
    body.setAttribute('data-rotate-msg', I18n.t('rotate_message'))
  }

  fetchDatabinForCartItemsCountAndMergeDatabin = async () => {
    try {
      await API.updateSlugAndMergeDatabin()
      const res = await API.fetchDatabin()
      const cart_items = _.get(res, 'data.data_bin.data.shopping_cart_items', [])

      const cart_total_items = this.calculateTotalCartItems(cart_items)

      this.setState({ cart_total_items })

      return res
    } catch(err) {
      throw err
    }
  }

  calculateTotalCartItems = (cart_items) => {
    return cart_items.reduce((total, item) => {
      total += item.quantity

      return total
    }, 0)
  }

  updateDatabinStatusToClosed = async () => {
    try {
      const databin_res = await this.fetchDatabinForCartItemsCountAndMergeDatabin()
      const databin_data = _.get(databin_res, 'data.data_bin.data', {})
      const upload_databin_data = {
        ...databin_data,
        status: 'closed'
      }
      // start rendering the cart after databin is merge
      this.setState({ fetch_databin_loading: false })
      API.updateDatabin(upload_databin_data)
    } catch(err) {
      console.log('update databin status to closed failed: ', err)
      // start rendering the cart after databin is merge
      this.setState({ fetch_databin_loading: false })
    }
  }

  checkOrdersStatus = async () => {
    const orders = API.getStorage('completed_orders') || []
    let errors = []

    this.setState({ loading_orders: true })

    orders.forEach((order) => {
      this.fetch_order_status_queue.add(() =>
        this.handleOrderStatusUpdate(order.order_id, order.slug_name)
      )
    })

    try {
      await this.fetch_order_status_queue.onEmpty()
    } catch(e) {
      console.log(e)
    } finally {
      this.setState({ loading_orders: false })
    }
  }

  handleOrderStatusUpdate = async (order_id, slug_name) => {
    const order_states_to_be_forgotten = [
      'completed',
      'void_in_progress',
      'void_completed',
      'cancelled',
      'declined'
    ]
    let orders = _.cloneDeep(this.state.orders)

    try {
      const res = await API.fetchOrderBySlugName(order_id, slug_name)
      const order = _.get(res, 'data.order', {})
      const { state: order_state } = order
      const shipment_state = order.shipments[0].state

      // remove order from storage and store as it is obsolete
      if (_.includes(order_states_to_be_forgotten, order_state)) {
        API.removeCompletedOrderFromStorage(order_id)
        _.remove(orders, o => o.id === order.id)
        // go to thank you page if all others are completed
        if (order_state === 'completed' && _.isEmpty(API.getStorage('completed_orders'))) {
          this.setState({ go_to_thank_you: true })
        }

        return
      }

      // display alert when order is ready to receive
      if (shipment_state === 'ready_to_receive') {
        this.setState({
          alert_message: <Text text={I18n.t('order_ready', { ticket_number: order.shipments[0].ticket_number })} />,
          order_exist: false
        }, this._flash.open('black', Config.get('ORDER_READY_FLASH_MESSAGE_DURATION')))
      }

      orders = this.addOrReplaceOrder(orders, order)
    } catch(err) {
      // remove order when 404 meaning the order is expired
      if (err.code === 404) {
        API.removeCompletedOrderFromStorage(order_id)
        _.remove(orders, o => o.id === order_id)
        return
      }

      throw err
    } finally {
      this.setState({ orders: orders })
    }
  }

  addOrReplaceOrder = (arr, obj) => {
    const new_arr = _.cloneDeep(arr)
    const index = new_arr.findIndex((e) => e.id === obj.id)

    if (index === -1) {
      new_arr.push(obj)
    } else {
      new_arr[index] = obj
    }

    return new_arr
  }

  kickOffTimerToFetchOrderState = () => {
    this.recursiveFetchOrdersState = setInterval(() => this.checkOrdersStatus(), Config.get('FETCH_ORDER_STATUS_INTERVAL'))
  }

  // determin active session is active and not expired yet
  isSessionActive = (active_session) => {
    if (_.isEmpty(active_session)) return false

    const is_expired = moment().isAfter(active_session.expires_at)
    return active_session.is_active && !is_expired
  }

  saveSystemSettingToStore = async () => {
    try {
      const fetch_system_settings_res = await API.fetchSystemSetting()
      const { system_settings } = fetch_system_settings_res.data

      this.setState({
        system_settings: _.reduce(system_settings, (result, setting) => {
          result[setting.name] = setting.value
          return result
        }, {})
      })
    } catch(err) {
      console.log('could not load system settings due to: ', err)
    } finally {
      this.setState({ loading_system_settings: false })
    }
  }

  saveToStore = (new_state, cb) => {
    this.setState({
      ...this.state,
      ...new_state
    }, cb)
  }

  hydrateStateWithLocalStorage = (state_key) => {
    // get the state value from localStorage
    const value = API.getStorage(state_key)

    !_.isUndefined(value) && this.setState({ [state_key]: value })
  }

  render() {
    const {
      alert_message = '',
      go_to_thank_you,
      go_to_order_history,
      loading_system_settings,
      fetch_databin_loading
    } = this.state

    return loading_system_settings || fetch_databin_loading
      ? <PageMiddleSpinner loading={true} />
      : <Provider value={{...this.state, flash: this._flash}}>
        <HashRouter>
          <AppStyled>
            <OfflineBanner />
            <Alert ref={ref => this._flash = ref} type='danger' message={alert_message} />
            <Switch>
              {
                go_to_thank_you &&
                  !_.includes(window.location.hash, 'thank_you') &&
                    <Redirect exact from="/:lang/(.*)" to="/:lang/thank_you" />
              }
              {
                go_to_thank_you &&
                  !_.includes(window.location.hash, 'thank_you') &&
                    <Redirect exact from="/:lang" to="/:lang/thank_you" />
              }
              {
                go_to_order_history &&
                  !_.includes(window.location.hash, 'order_history') &&
                    <Redirect exact from='/:lang/(.*)' to="/:lang/order_history" />
              }
              {
                go_to_order_history &&
                  !_.includes(window.location.hash, 'order_history') &&
                    <Redirect exact from='/:lang' to="/:lang/order_history" />
              }
              <Route exact path={`/:lang(${I18n.getAvailableLocales().join('|')})?`} component={Cart} />
              <Route path="/:lang/order_history" component={OrderHistory} />
              <Route path="/:lang/search" component={ProductSearchResult} />
              <Route path="/:lang/products" component={ProductGroups} />
              <Route path="/:lang/thank_you" component={ThankYou} />
            </Switch>
          </AppStyled>
        </HashRouter>
      </Provider>
  }
}

export default withStore(App);



// WEBPACK FOOTER //
// ./src/App.js